// Katalog sprzętu dla wyboru modelu (`components/katalog/WyszukiwarkaModelu.jsx`: projekt dachu 3D
// i strona zgodności): producenci,
// serie, listy modeli, podpowiedzi wyszukiwarki i dane jednego modelu dla kategorii
// 'moduly' | 'falownik' | 'magazyn'. Same zapytania; zamiana na kształty projektu jest
// w `lib/projekt3d/katalog.js`.
//
// Odpowiedzi trzymamy w pamięci po adresie (razem z zapytaniami w toku): krok zwinięty
// i otwarty ponownie nie pyta serwera drugi raz. Błąd nie zostaje w pamięci, więc ponowna
// próba pyta od nowa. Funkcje rzucają wyjątek przy błędzie sieci albo serwera.
import { apiFetch } from './apiFetch'
import { magazineManufacturers } from '../filtersSchema'

const apiUrl = process.env.REACT_APP_API_URL || '/api/'

/** Najwięcej modeli jednego producenta na liście (2026-10: najwięcej ma Deye, 122 falowniki). */
export const LIMIT_MODELI = 300
/** Najwięcej podpowiedzi magazynów (moduły i falowniki ogranicza sam `szukaj_modeli/`). */
export const LIMIT_PODPOWIEDZI = 8

// Nazwy kategorii w poszczególnych endpointach backendu.
const KATEGORIE = {
  moduly: { lista: 'panel_models', opcje: 'panel', szukaj: 'panel', model: 'moduly', zakres: ['min_power', 'max_power'] },
  falownik: { lista: 'inverter_models', opcje: 'inwerter', szukaj: 'inwerter', model: 'falownik', zakres: ['min_power', 'max_power'] },
  // Magazyny nie mają `opcje_modeli/` ani `szukaj_modeli/`: producenci z filtrów, szukanie po liście.
  magazyn: { lista: 'magazine_models', opcje: null, szukaj: null, model: 'magazyn', zakres: ['min_capacity', 'max_capacity'] },
}

const kategoriaApi = (kategoria) => {
  const k = KATEGORIE[kategoria]
  if (!k) throw new Error(`Nieznana kategoria katalogu: ${kategoria}`)
  return k
}

// Wiele różnych fraz w wyszukiwarce: najstarsze wpisy wypadają po przekroczeniu limitu.
const POJEMNOSC_PAMIECI = 300
const pamiec = new Map()

const pobierz = (sciezka) => {
  const adres = `${apiUrl}${sciezka}`
  const zapamietana = pamiec.get(adres)
  if (zapamietana) return zapamietana
  const obietnica = apiFetch(adres).then((res) => {
    if (!res.ok) throw new Error(`Katalog odpowiedział kodem ${res.status}`)
    return res.json()
  })
  pamiec.set(adres, obietnica)
  if (pamiec.size > POJEMNOSC_PAMIECI) pamiec.delete(pamiec.keys().next().value)
  obietnica.catch(() => { if (pamiec.get(adres) === obietnica) pamiec.delete(adres) })
  return obietnica
}

/** Czyści pamięć odpowiedzi (testy, ewentualnie ręczne odświeżenie). */
export const wyczyscPamiecKatalogu = () => pamiec.clear()

/** Producenci alfabetycznie. Moduły i falowniki z `opcje_modeli/`, magazyny z listy filtrów. */
export const pobierzProducentow = async (kategoria) => {
  const k = kategoriaApi(kategoria)
  const lista = k.opcje
    ? ((await pobierz(`opcje_modeli/?${new URLSearchParams({ kategoria: k.opcje })}`)).producenci || [])
    : Object.keys(magazineManufacturers)
  return [...lista].sort((a, b) => a.localeCompare(b, 'pl'))
}

/** Serie jednego producenta: [{ value, count }]. Backend liczy tylko modele z ceną. */
export const pobierzSerie = async (kategoria, producent) => {
  const k = kategoriaApi(kategoria)
  if (!producent) return []
  const d = await pobierz(`${k.lista}/series_options?${new URLSearchParams({ manufacturer: producent })}`)
  return d.series || []
}

/**
 * Modele z katalogu, od najczęściej oferowanych: { items, count }. `mocOd` i `mocDo` to W
 * (moduły), kW (falowniki) albo kWh (magazyny). Wiersze listy modułów nie mają wymiarów,
 * te są dopiero w `pobierzModel`.
 */
export const pobierzModele = async (kategoria, {
  producent = null, seria = null, mocOd = null, mocDo = null, limit = LIMIT_MODELI, typ = null,
} = {}) => {
  const k = kategoriaApi(kategoria)
  const par = new URLSearchParams()
  if (producent) par.append('manufacturers', producent)
  // Typ z katalogu, np. 'Hybrydowy': strona zgodności pokazuje tylko falowniki z wejściem baterii.
  if (typ) par.append('types', typ)
  if (seria) par.append('series_in', seria)
  if (mocOd != null) par.set(k.zakres[0], mocOd)
  if (mocDo != null) par.set(k.zakres[1], mocDo)
  par.set('order_by', 'offers')
  par.set('limit', limit)
  const d = await pobierz(`${k.lista}/?${par}`)
  const items = d.items || []
  return { items, count: d.count ?? items.length }
}

/**
 * Podpowiedzi do pola wyszukiwania jako surowe wiersze: wyniki `szukaj_modeli/` (moduły,
 * falowniki) albo wiersze `magazine_models/?search=` (magazyny). Wspólny kształt robi
 * `pozycjaListy` w `lib/projekt3d/katalog.js`.
 */
export const szukajWKatalogu = async (kategoria, fraza, { typ = null } = {}) => {
  const k = kategoriaApi(kategoria)
  const q = (fraza || '').trim()
  if (!q) return []
  if (k.szukaj) {
    const par = new URLSearchParams({ kategoria: k.szukaj, q })
    if (typ) par.set('rodzaj', typ)
    return (await pobierz(`szukaj_modeli/?${par}`)).wyniki || []
  }
  const par = new URLSearchParams({ search: q, limit: LIMIT_PODPOWIEDZI })
  if (typ) par.set('types', typ)
  return (await pobierz(`${k.lista}/?${par}`)).items || []
}

/**
 * Dane jednego modelu. Moduł: pełna strona modelu (`get_panel_model/{id}`, ok. 15 kB z ofertami),
 * bo tylko tam są wymiary, typy, kolor ramy i liczba ogniw. Falownik i magazyn: skrót
 * `sprawdz_oferte/model` (moc, fazy, typ; pojemność, moc rozładowania, `karta_magazynu`).
 */
export const pobierzModel = async (kategoria, id) => {
  const k = kategoriaApi(kategoria)
  if (kategoria === 'moduly') return pobierz(`get_panel_model/${encodeURIComponent(id)}`)
  return pobierz(`sprawdz_oferte/model?${new URLSearchParams({ kategoria: k.model, model_id: id })}`)
}
